import React, { useRef, useState, useLayoutEffect } from "react";
import { fontCssStyle } from "./utils/payslipFonts";
import DOLLUZ_Full_Logo from "./assets/img/DOLLUZ_Full_Logo.png";
import signature_img from "./assets/img/signature.png";
import "./Payslip_Management.css";

// Shared, PDF-accurate payslip preview body. Rendered identically by:
//   • Payslip_Management → PreviewModal (fetches a saved payslip by id)
//   • Payslip_Generate   → pre-generation preview (builds `data` from the
//                          in-memory salary structure + adjustments)
//
// Anything that changes here must be mirrored in generatePayslipPDF()
// (Payslip_Management.js). `data` is the normalized payslip shape - the same
// object the /management/detail endpoint returns.

const MONTH_NAMES = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

// Integer with commas (no decimals) - matches the PDF generator's `ni` helper
export const fmtInt = (v) => Math.round(Number(v || 0)).toLocaleString("en-IN");

// 2019/12/02 - matches the PDF generator's formatDOJ
export const formatDOJ = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt)) return "-";
    return `${dt.getFullYear()}/${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}`;
};

export default function PayslipPreviewBody({ data }) {
    // Preview-only: anchor the watermark's TOP edge to the VERTICAL MIDDLE of the
    // employee-details table, so the mark always begins at the half-height of that
    // block no matter how many earning/deduction rows follow. (The generated PDF
    // keeps its own anchoring - this is not mirrored there.) Measured after
    // layout, before paint.
    // Nudge the mark up a little from the exact middle of the emp-details block.
    const WM_LIFT_PX = 30;
    const paperRef = useRef(null);
    const empTableRef = useRef(null);
    const [wmTop, setWmTop] = useState(null);
    useLayoutEffect(() => {
        const measure = () => {
            if (!paperRef.current || !empTableRef.current) return;
            const c = paperRef.current.getBoundingClientRect();
            const r = empTableRef.current.getBoundingClientRect();
            // Middle of the emp-details table, lifted up a little.
            setWmTop(r.top - c.top + r.height / 2 - WM_LIFT_PX);
        };
        measure();
        window.addEventListener("resize", measure);
        // Re-measure whenever the paper reflows - crucially after the logo /
        // signature images finish loading, which push the emp-table down. Without
        // this the anchor is measured against a pre-image layout, so the mark lands
        // too high when the images aren't cached yet (the Generate-vs-Management
        // mismatch). The observer fires again once the real layout settles.
        let ro;
        if (typeof ResizeObserver !== "undefined" && paperRef.current) {
            ro = new ResizeObserver(() => measure());
            ro.observe(paperRef.current);
        }
        return () => {
            window.removeEventListener("resize", measure);
            if (ro) ro.disconnect();
        };
    }, [data]);

    if (!data) return null;

    const items = Array.isArray(data.items) ? data.items : [];
    const earnItems = items
        .filter(i => i.component_type === "Earning")
        .map(i => ({ label: i.component_name, amount: i.amount }));
    if (parseFloat(data.bonus) > 0) earnItems.push({ label: "Bonus", amount: data.bonus });
    if (parseFloat(data.other_earnings) > 0) {
        earnItems.push({ label: data.other_earnings_note || "Other Earnings", amount: data.other_earnings });
    }
    const dedItems = items
        .filter(i => i.component_type === "Deduction")
        .map(i => ({ label: i.component_name, amount: i.amount }));
    if (parseFloat(data.lop_deduction) > 0) dedItems.push({ label: "LOP deduction", amount: data.lop_deduction });
    if (parseFloat(data.loan_deduction) > 0) dedItems.push({ label: "Salary advance (rev)", amount: data.loan_deduction });
    if (parseFloat(data.other_deductions) > 0) {
        dedItems.push({ label: data.other_deductions_note || "Other Deductions", amount: data.other_deductions });
    }
    const maxRows = Math.max(earnItems.length, dedItems.length);

    const empName = (`${data.emp_first_name || ""} ${data.emp_last_name || ""}`.trim()) || data.emp_name || "";
    const empCode = data.emp_code || data.emp_id || "-";
    const panNumber = data.pan_number || "-";
    const location = data.location || data.emp_location || "-";
    const designation = data.designation || data.job_position || "-";
    const department = data.department || data.emp_department || "-";
    const bankDetails = data.bank_details || data.bank_account_number || "-";
    // Verbatim textarea content - no dedup, no filtering.
    const addressLines = (data.company_address ||
        "#254/3, Sree Narayana Complex, C Block,\nSarathy Nagar, Velachery, Chennai 600042")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .slice(0, 6);
    // LOP renders as integer when no fractional part
    const lopText = (() => {
        const n = parseFloat(data.lop_days) || 0;
        return n % 1 === 0 ? String(parseInt(n, 10)) : String(n);
    })();

    return (
        <div className="pm-prev-pdf" style={fontCssStyle(data.font)} ref={paperRef}>
            {/* Full DOLLUZ CORP logo watermark behind the body, drawn as TWO
                stacked copies: the top sharp, the bottom blurred.
                A single <img> can't be partially blurred - CSS `filter` applies
                to the whole element - so the pair carry complementary masks
                that hand over across the same band. Their opacities sum to
                roughly one through the transition, so there's no bright or
                dark seam where they meet. Preview only; the PDF watermark is
                untouched. */}
            <img
                src={DOLLUZ_Full_Logo}
                alt=""
                className="pm-prev-pdf-watermark pm-prev-pdf-watermark--sharp"
                style={wmTop != null ? { top: wmTop } : undefined}
            />
            <img
                src={DOLLUZ_Full_Logo}
                alt=""
                className="pm-prev-pdf-watermark pm-prev-pdf-watermark--blur"
                style={wmTop != null ? { top: wmTop } : undefined}
            />

            {/* Gold accent line (matches PDF) */}
            <div className="pm-prev-pdf-accent" />

            {/* Full logo - eagle + wordmark + tagline in one image */}
            <div className="pm-prev-pdf-letterhead">
                <img src={DOLLUZ_Full_Logo} alt="" className="pm-prev-pdf-eagle" />
            </div>

            {/* Title - no separator line above, no payslip-number
                caption below (mirrors the reference PDF). */}
            <div className="pm-prev-pdf-title">
                PAYSLIP FOR THE MONTH - {MONTH_NAMES[data.pay_month].toUpperCase()} {data.pay_year}
            </div>

            {/* Employee info - 4 cols × 5 rows (exact PDF order) */}
            <table className="pm-prev-pdf-emptable" ref={empTableRef}>
                <tbody>
                    <tr>
                        <td className="pm-prev-pdf-label">NAME:</td>
                        <td className="pm-prev-pdf-value">{empName || "-"}</td>
                        <td className="pm-prev-pdf-label">PAN:</td>
                        <td className="pm-prev-pdf-value">{panNumber}</td>
                    </tr>
                    <tr>
                        <td className="pm-prev-pdf-label">EMPLOYEE CODE:</td>
                        <td className="pm-prev-pdf-value">{empCode}</td>
                        <td className="pm-prev-pdf-label">LOCATION:</td>
                        <td className="pm-prev-pdf-value">{location}</td>
                    </tr>
                    <tr>
                        <td className="pm-prev-pdf-label">DESIGNATION:</td>
                        <td className="pm-prev-pdf-value">{designation}</td>
                        <td className="pm-prev-pdf-label">BANK DETAILS:</td>
                        <td className="pm-prev-pdf-value">{bankDetails}</td>
                    </tr>
                    <tr>
                        <td className="pm-prev-pdf-label">DEPARTMENT:</td>
                        <td className="pm-prev-pdf-value">{department}</td>
                        <td className="pm-prev-pdf-label">DAYS PAYABLE:</td>
                        <td className="pm-prev-pdf-value">{data.days_in_month || 30}</td>
                    </tr>
                    <tr>
                        <td className="pm-prev-pdf-label">DOJ:</td>
                        <td className="pm-prev-pdf-value">{formatDOJ(data.doj)}</td>
                        <td className="pm-prev-pdf-label">LOP:</td>
                        <td className="pm-prev-pdf-value">{lopText}</td>
                    </tr>
                </tbody>
            </table>

            {/* Earnings | Deductions - zipped 4-col table with yellow head */}
            <table className="pm-prev-pdf-pay">
                <thead>
                    <tr>
                        <th className="pm-prev-pdf-pay-head pm-prev-pdf-pay-head--label">Earnings</th>
                        <th className="pm-prev-pdf-pay-head pm-prev-pdf-pay-head--amt">Monthly<br/>in INR</th>
                        <th className="pm-prev-pdf-pay-head pm-prev-pdf-pay-head--label">Deductions</th>
                        <th className="pm-prev-pdf-pay-head pm-prev-pdf-pay-head--amt">Monthly<br/>in INR</th>
                    </tr>
                </thead>
                <tbody>
                    {(() => {
                        // Pre-compute which row indices are blank on each side so we
                        // can also detect "row above / below on the same side is blank"
                        // and merge consecutive empty rows into one box.
                        const isBlank = (it) =>
                            !it || (!it.label && (it.amount === null || it.amount === undefined));
                        const eBlankRow = Array.from({ length: maxRows }, (_, i) => isBlank(earnItems[i]));
                        const dBlankRow = Array.from({ length: maxRows }, (_, i) => isBlank(dedItems[i]));

                        return Array.from({ length: maxRows }).map((_, i) => {
                            const e = earnItems[i] || { label: "", amount: null };
                            const d = dedItems[i] || { label: "", amount: null };
                            const eBlank = eBlankRow[i];
                            const dBlank = dBlankRow[i];
                            const eAbove = eBlank && i > 0 && eBlankRow[i - 1];
                            const eBelow = eBlank && i < maxRows - 1 && eBlankRow[i + 1];
                            const dAbove = dBlank && i > 0 && dBlankRow[i - 1];
                            const dBelow = dBlank && i < maxRows - 1 && dBlankRow[i + 1];

                            const eCls = (which) => {
                                if (!eBlank) return "";
                                let cls = which === "first"
                                    ? "pm-prev-pdf-pay-cell--blank-first"
                                    : "pm-prev-pdf-pay-cell--blank-second";
                                if (eAbove) cls += " pm-prev-pdf-pay-cell--blank-top-hidden";
                                if (eBelow) cls += " pm-prev-pdf-pay-cell--blank-bottom-hidden";
                                return cls;
                            };
                            const dCls = (which) => {
                                if (!dBlank) return "";
                                let cls = which === "first"
                                    ? "pm-prev-pdf-pay-cell--blank-first"
                                    : "pm-prev-pdf-pay-cell--blank-second";
                                if (dAbove) cls += " pm-prev-pdf-pay-cell--blank-top-hidden";
                                if (dBelow) cls += " pm-prev-pdf-pay-cell--blank-bottom-hidden";
                                return cls;
                            };

                            return (
                                <tr key={i}>
                                    <td className={`pm-prev-pdf-pay-cell ${eCls("first")}`}>{e.label}</td>
                                    <td className={`pm-prev-pdf-pay-cell pm-prev-pdf-pay-amt ${eCls("second")}`}>
                                        {e.amount !== null && e.amount !== undefined ? fmtInt(e.amount) : ""}
                                    </td>
                                    <td className={`pm-prev-pdf-pay-cell ${dCls("first")}`}>{d.label}</td>
                                    <td className={`pm-prev-pdf-pay-cell pm-prev-pdf-pay-amt ${dCls("second")}`}>
                                        {d.amount !== null && d.amount !== undefined ? fmtInt(d.amount) : ""}
                                    </td>
                                </tr>
                            );
                        });
                    })()}
                    <tr className="pm-prev-pdf-pay-total">
                        <td>Total Earnings</td>
                        <td className="pm-prev-pdf-pay-amt">{fmtInt(data.gross_salary)}</td>
                        <td>Total Deductions</td>
                        <td className="pm-prev-pdf-pay-amt">{fmtInt(data.total_deductions)}</td>
                    </tr>
                </tbody>
            </table>

            {/* NET PAY - below the table (outside the border), aligned to
                the Deductions column via the fixed 36/14/36/14 layout. */}
            <div className="pm-prev-pdf-netpay">
                <span className="pm-prev-pdf-netpay-label">NET PAY</span>
                <span className="pm-prev-pdf-netpay-val">{fmtInt(data.net_pay)}</span>
            </div>

            {/* Note - "Note:" bold + sentence normal, both at the
                same size so the line reads as one continuous piece. */}
            <div className="pm-prev-pdf-note">
                <span className="pm-prev-pdf-note-label">Note:</span>{" "}
                <span className="pm-prev-pdf-note-body">Income Tax will be deducted as per income Tax Act 1961 and will be borne by the employee</span>
            </div>

            {/* Footer: company address left, signature right */}
            <div className="pm-prev-pdf-footer-line" />
            <div className="pm-prev-pdf-footer">
                <div className="pm-prev-pdf-footer-left">
                    <div className="pm-prev-pdf-company">Dolluz Corporation (OPC) Private Limited</div>
                    {addressLines.map((line, idx) => (
                        <div className="pm-prev-pdf-address" key={idx}>{line}</div>
                    ))}
                </div>
                <div className="pm-prev-pdf-footer-right">
                    <img src={signature_img} alt="" className="pm-prev-pdf-signature" />
                    <div className="pm-prev-pdf-sigtext">AUTHORIZED SIGNATURE</div>
                </div>
            </div>
        </div>
    );
}
